import { useContext, useEffect } from "react";
import { EntityContext } from "../../../../contexts/EntityContext";
import {
  getLocalStorageData,
  clearLocalStorageData,
} from "../../../../shared/utils/localStorage";
import { Box, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import CoinsIcon from "../../../../assets/img/money-coins.svg";
import useQuery from "../../../../shared/components/hooks/useQuery";
import {
  formatChange,
  formatDateRange,
} from "../../../../shared/utils/formatter";
import { PropertyContext } from "../../../../contexts/PropertyContext";
import DashboardInsights from "../dashboard-insights";
import "./styles.css";

const Dashboard = () => {
  const { setEntityName } = useContext(EntityContext);
  const { propertyCode, propertyName } = useContext(PropertyContext);

  useEffect(() => {
    getLocalStorageData("token");
    setEntityName({ entityName: "Dashboard", entityTitle: "Dashboard" });
  }, [setEntityName]);

  useEffect(() => {
    const token = getLocalStorageData("token");
    if (!token) {
      clearLocalStorageData();
      window.location.href = "/login";
    }
  }, []);

  const startEndAdrQuery = useQuery(
    `analytics/weekly-avg-adr?propertyId=${propertyCode}`,
    undefined,
    null,
    { enabled: !!propertyCode }
  );

  const occupancyQuery = useQuery(
    `analytics/weekly-avg-occupancy?propertyId=${propertyCode}`,
    undefined,
    null,
    { enabled: !!propertyCode }
  );

  const revparActualQuery = useQuery(
    `analytics/weekly-avg-revpar-actual?propertyId=${propertyCode}`,
    undefined,
    null,
    { enabled: !!propertyCode }
  );

  const revparLyQuery = useQuery(
    `analytics/weekly-avg-revpar-ly?propertyId=${propertyCode}`,
    undefined,
    null,
    { enabled: !!propertyCode }
  );

  const revparWowQuery = useQuery(
    `analytics/weekly-preweek-avg-revpar-wow?propertyId=${propertyCode}`,
    undefined,
    null,
    { enabled: !!propertyCode }
  );
  const startEndAdr = (startEndAdrQuery as any).data;
  const occupancy = (occupancyQuery as any).data;
  const revparActual = (revparActualQuery as any).data;
  const revparLy = (revparLyQuery as any).data;
  const revparWow = (revparWowQuery as any).data;

  const renderValue = (
    loading: boolean,
    value: any,
    formatter?: (v: any) => React.ReactNode
  ) =>
    loading ? (
      <CircularProgress size={16} />
    ) : formatter ? (
      formatter(value)
    ) : (
      value
    );

  return (
    <>
      <Box
        sx={{
          pt: { xs: 2, sm: -3 },
          pl: { xs: 2, sm: 2.67 },
          pr: { xs: 2, sm: 2.67 },
          pb: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            p: { xs: 0, sm: 3 },
            width: "100%",
            backgroundColor: {
              xs: "transparent",
              md: "#9E9E9E1A",
            },
            borderRadius: {
              xs: "0",
              md: "12px",
            },

            margin: { xs: "0 auto" },
            maxWidth: { xs: "100%", sm: "none" },
          }}
        >
          <Box className="mobTopBox">
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                mb: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: {
                  xs: "start",
                  sm: "start",
                  md: "unset",
                },
                gap: {
                  xs: "0",
                  md: "1",
                },
                fontSize: {
                  xs: "1rem",
                  sm: "1.25rem",
                  md: "1.5rem",
                },
                flexWrap: "wrap",
              }}
            >
              Weekly Performance Summary
              {propertyName && (
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  — {propertyName}
                </span>
              )}
            </Typography>

            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: { xs: 0, md: 2 } }}
            >
              {startEndAdrQuery.loading ? (
                <CircularProgress size={16} />
              ) : startEndAdr?.start_date && startEndAdr?.end_date ? (
                `${formatDateRange(
                  startEndAdr.start_date,
                  startEndAdr.end_date
                )} (Analysis of KPIs)`
              ) : (
                "Date unavailable (Analysis of KPIs)"
              )}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {/* LEFT CARD */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: {
                    xs: 0,
                    md: 3,
                  },
                  mt: {
                    xs: 2,
                    md: 0,
                  },
                  height: "100%",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: { xs: "row", md: "column" },
                  justifyContent: {
                    xs: "space-between",
                    md: "unset",
                  },
                  gap: {
                    xs: 2,
                    md: 0,
                  },
                }}
              >
                <Typography className="averageOccupancy">
                  <strong>Average Occupancy:</strong>{" "}
                  {renderValue(
                    occupancyQuery.loading,
                    occupancy?.avg_occupancy,
                    (v) => `${Number(v || 0).toFixed(2)}%`
                  )}
                </Typography>

                <Typography
                  sx={{
                    mt: {
                      xs: 0,
                      md: 2,
                    },
                  }}
                  className="adr"
                >
                  <strong>Average Daily Rate (ADR):</strong>{" "}
                  {renderValue(
                    startEndAdrQuery.loading,
                    startEndAdr?.avg_adr,
                    (v) => `$${Number(v || 0).toFixed(2)}`
                  )}
                </Typography>
              </Paper>
            </Grid>

            {/* RIGHT CARD */}
            <Grid item xs={12} md={6}>
              <Paper
                elevation={1}
                sx={{
                  p: {
                    xs: 0,
                    md: 3,
                  },
                  height: "100%",
                  borderRadius: "12px",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  className="RevPar"
                >
                  <Box
                    component="img"
                    src={CoinsIcon}
                    alt="Coins"
                    sx={{ width: 100, height: 100, mr: 3 }}
                  />
                  <Box>
                    <Typography>
                      RevPAR:{" "}
                      {renderValue(
                        revparActualQuery.loading,
                        revparActual?.avg_revpar_actual,
                        (v) => (
                          <>
                            <strong>${Number(v || 0).toFixed(2)}</strong> (
                            {renderValue(
                              revparLyQuery.loading,
                              revparLy?.avg_revpar_ly,
                              (v) => `${formatChange(Number(v))} vs LY`
                            )}
                            )
                          </>
                        )
                      )}
                    </Typography>

                    <Typography sx={{ mt: 2 }}>
                      RevPAR:{" "}
                      {renderValue(
                        revparWowQuery.loading,
                        revparWow?.preweek_avg_revpar,
                        (v) => (
                          <>
                            <strong>${Number(v || 0).toFixed(2)}</strong> (
                            {formatChange(revparWow?.revpar_wow_percent)} WoW)
                          </>
                        )
                      )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Box>

      <Box
        className="dashBoardMain"
        sx={{
          pt: { xs: 2, sm: -3 },
          pl: { xs: 2, sm: 6 },
          pr: { xs: 2, sm: 5 },
          pb: 2,
          width: "100%",
        }}
      >
        <Box
          sx={{
            p: { xs: 0, sm: 3 },
            width: "100%",
            backgroundColor: { xs: "transparent", md: "#9E9E9E1A" },
            borderRadius: "12px",
            margin: { xs: "0 auto" },
            maxWidth: { xs: "100%", sm: "none" },
          }}
        >
          <Grid item xs={12}>
            <DashboardInsights />
          </Grid>
        </Box>
      </Box>
    </>
  );
};

export default Dashboard;
